import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { SyncSelection } from "../../domain/entities/sharepoint"
import { displayPathParts, selKey } from "./sharepoint-file-list"

type Props = {
  entries: SyncSelection[]
  onRemove: (itemId: string) => void
  onClear: () => void
  onSync: () => void
  syncDisabled: boolean
  heightClass?: string
}

export const SelectedPane = ({
  entries,
  onRemove,
  onClear,
  onSync,
  syncDisabled,
  heightClass = "h-[55vh]",
}: Props) => (
  <div className={cn("flex flex-col rounded border", heightClass)}>
    <div className="flex items-center justify-between border-b px-3 py-2 text-sm">
      <span className="font-medium">Selected ({entries.length})</span>
      <button
        className="cursor-pointer text-xs text-muted-foreground hover:underline disabled:cursor-default disabled:opacity-50"
        onClick={onClear}
        disabled={entries.length === 0}
      >
        Clear all
      </button>
    </div>

    <div className="flex-1 overflow-y-auto">
      {entries.length === 0 ? (
        <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
          No items selected — tick files or folders on the left.
        </div>
      ) : (
        entries.map((e) => {
          const key = selKey(e.driveId, e.itemId)
          const { prefix, name } = displayPathParts(e.path)
          return (
            <div key={key} className="flex items-center gap-2 border-b px-3 py-2 text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label={`Remove ${e.name}`}
                    className="shrink-0 cursor-pointer rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={() => onRemove(key)}
                  >
                    <X className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">Remove</TooltipContent>
              </Tooltip>
              <span className="shrink-0 text-base leading-none">
                {e.isFolder ? "📁" : "📄"}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex min-w-0 flex-1 items-center overflow-hidden">
                    {prefix ? (
                      <span className="min-w-0 truncate text-muted-foreground">
                        {prefix}
                      </span>
                    ) : null}
                    <span className="shrink-0">{name}</span>
                    {e.isFolder ? (
                      <span className="ml-1 shrink-0 text-xs text-muted-foreground">
                        (all files inside)
                      </span>
                    ) : null}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{e.path}</TooltipContent>
              </Tooltip>
            </div>
          )
        })
      )}
    </div>

    <div className="border-t p-2">
      <Button className="w-full" onClick={onSync} disabled={syncDisabled}>
        Sync ({entries.length})
      </Button>
    </div>
  </div>
)
